"use client";

import {
	type DefaultImageProps,
	type DefaultLinkProps,
	Heading,
	Image,
	type ImageProps,
	Link,
	type LinkProps
} from "@pamperedchef/design-system";
import { className as classNameHelper } from "@/app/utilities/className";
import { useId } from "react";

import * as styles from "./CategoryCard.module.scss";

export interface CategorySingleCardsProps<
	ImageComponentProps = DefaultImageProps,
	LinkComponentProps = DefaultLinkProps
> {
	link: LinkProps<LinkComponentProps>;
	image: ImageProps<ImageComponentProps>;
	title: string;
}

export interface CategoryCardProps<
	ImageComponentProps = DefaultImageProps,
	LinkComponentProps = DefaultLinkProps
> extends React.HTMLAttributes<HTMLDivElement> {
	/** An array of React components representing the cards */
	cards: CategorySingleCardsProps<ImageComponentProps, LinkComponentProps>[];
	/** Component title */
	headerText?: string;
	/** Class name for the focus state demo */
	focusState?: string;
	/** An object with optional class names for different parts of the carousel */
	classNames?: {
		/** Class name for the cards container */
		cards?: string;
		/** Class name for individual cards */
		card?: string;
		/** Class name for the slideText  */
		cardText?: string;
		/** Class name for the cardImg  */
		cardImg?: string;
	};
}

/**
 * Card Carousel component
 */
export function CategoryCard<
	ImageComponentProps = DefaultImageProps,
	LinkComponentProps = DefaultLinkProps
>(
	props: CategoryCardProps<ImageComponentProps, LinkComponentProps>
): JSX.Element {
	const id = useId();
	/** Applies Logic for the 6+ layout variation for tablet devices */
	let showVersion = "tabletRow";

	if (props.cards.length > 5) {
		showVersion = "tabletBreak";
	}

	return (
		<div
			style={props.style}
			{...classNameHelper(styles["categoryCards"], props.className)}
		>
			<Heading
				tag="h3"
				styledAs="h1"
				id={`category-card-header-${id}`}
				className={styles.headerText}
			>
				{props.headerText}
			</Heading>
			<div
				{...classNameHelper(
					styles["cards"],
					styles[showVersion],
					props.classNames?.cards as string
				)}
			>
				{props.cards.map((card, idx) => {
					return (
						<Link key={idx} {...card.link}>
							<div
								{...classNameHelper(
									styles["card"],
									props.focusState && styles["focus"],
									props.classNames?.card as string
								)}
							>
								<div {...classNameHelper(styles["cardImg"])}>
									<Image<ImageComponentProps>
										{...card.image}
										alt={
											card.image.alt ||
											"Category Card Image"
										}
									/>
								</div>
								<div {...classNameHelper(styles["cardText"])}>
									{card.title}
								</div>
							</div>
						</Link>
					);
				})}
			</div>
		</div>
	);
}
